import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import type {
  CierreActivoFijo,
  FixedAssetCategory,
  FixedAssetDto,
  FixedAssetInput,
} from "@fiscalo/shared-types";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  createFixedAsset,
  deleteFixedAsset,
  updateFixedAsset,
} from "@/services/cierreService";

const CATEGORY_LABELS: Record<FixedAssetCategory, string> = {
  inmuebles: "Inmuebles",
  muebles_utiles: "Muebles y útiles",
  herramientas: "Herramientas",
  equipos_informatica: "Equipos de informática",
  vehiculos: "Vehículos",
  intangibles: "Intangibles",
  otros: "Otros",
};

const DEFAULT_RATE: Record<FixedAssetCategory, number> = {
  inmuebles: 2,
  muebles_utiles: 10,
  herramientas: 20,
  equipos_informatica: 20,
  vehiculos: 20,
  intangibles: 20,
  otros: 10,
};

const CATEGORIES = Object.keys(CATEGORY_LABELS) as FixedAssetCategory[];

function money(n: number): string {
  return n.toLocaleString("es-UY", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function parseNum(raw: string): number {
  const n = parseFloat(raw.replace(/\./g, "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

interface FormState {
  category: FixedAssetCategory;
  name: string;
  acquisitionYear: string;
  costContableUyu: string;
  depreciationRatePct: string;
  openingAccumDepContableUyu: string;
  revaluationBaseUyu: string;
  openingAccumDepFiscalUyu: string;
  isLand: boolean;
}

function emptyForm(): FormState {
  return {
    category: "muebles_utiles",
    name: "",
    acquisitionYear: String(new Date().getFullYear()),
    costContableUyu: "",
    depreciationRatePct: String(DEFAULT_RATE.muebles_utiles),
    openingAccumDepContableUyu: "0",
    revaluationBaseUyu: "",
    openingAccumDepFiscalUyu: "",
    isLand: false,
  };
}

function formFromAsset(a: FixedAssetDto): FormState {
  return {
    category: a.category,
    name: a.name,
    acquisitionYear: String(a.acquisitionYear),
    costContableUyu: String(a.costContableUyu),
    depreciationRatePct: String(a.depreciationRatePct),
    openingAccumDepContableUyu: String(a.openingAccumDepContableUyu),
    revaluationBaseUyu:
      a.revaluationBaseUyu != null ? String(a.revaluationBaseUyu) : "",
    openingAccumDepFiscalUyu:
      a.openingAccumDepFiscalUyu != null
        ? String(a.openingAccumDepFiscalUyu)
        : "",
    isLand: a.isLand,
  };
}

function toInput(f: FormState): FixedAssetInput {
  return {
    category: f.category,
    name: f.name.trim(),
    acquisitionYear: parseInt(f.acquisitionYear, 10),
    costContableUyu: parseNum(f.costContableUyu),
    depreciationRatePct: parseNum(f.depreciationRatePct),
    openingAccumDepContableUyu: parseNum(f.openingAccumDepContableUyu),
    revaluationBaseUyu: f.revaluationBaseUyu.trim()
      ? parseNum(f.revaluationBaseUyu)
      : null,
    openingAccumDepFiscalUyu: f.openingAccumDepFiscalUyu.trim()
      ? parseNum(f.openingAccumDepFiscalUyu)
      : null,
    isLand: f.isLand,
  };
}

/**
 * Fixed-asset register for the cierre fiscal. The accountant maintains the
 * assets (cost, opening balances, rate); the backend computes annual + fiscal
 * depreciation and the revaluation that feed the balance sheet.
 */
export function ActivoFijoCard({
  tenantId,
  fiscalYear,
  activoFijo,
  coefRevaluacion,
}: {
  tenantId: string;
  fiscalYear: number;
  activoFijo: CierreActivoFijo;
  coefRevaluacion: number;
}): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<FixedAssetDto | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());

  const invalidate = (): Promise<void> =>
    queryClient.invalidateQueries({
      queryKey: ["accountant", "cierre", tenantId, fiscalYear],
    });

  const save = useMutation({
    mutationFn: () => {
      const input = toInput(form);
      if (!input.name) throw new Error(t("cierre.af.nameRequired", "Falta el nombre"));
      return editing
        ? updateFixedAsset(tenantId, editing.id, input)
        : createFixedAsset(tenantId, input);
    },
    onSuccess: async () => {
      setOpen(false);
      setEditing(null);
      await invalidate();
      toast.success(t("cierre.af.saved", "Bien guardado"));
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : t("cierre.af.error", "No pudimos guardar"));
    },
  });

  const remove = useMutation({
    mutationFn: (assetId: string) => deleteFixedAsset(tenantId, assetId),
    onSuccess: async () => {
      await invalidate();
      toast.success(t("cierre.af.deleted", "Bien eliminado"));
    },
    onError: () => toast.error(t("cierre.af.error", "No pudimos guardar")),
  });

  const openAdd = (): void => {
    setEditing(null);
    setForm(emptyForm());
    setOpen(true);
  };
  const openEdit = (a: FixedAssetDto): void => {
    setEditing(a);
    setForm(formFromAsset(a));
    setOpen(true);
  };

  const setCategory = (category: FixedAssetCategory): void =>
    setForm((f) => ({
      ...f,
      category,
      depreciationRatePct:
        f.depreciationRatePct === "" ||
        f.depreciationRatePct === String(DEFAULT_RATE[f.category])
          ? String(DEFAULT_RATE[category])
          : f.depreciationRatePct,
    }));

  const { rows, totals } = activoFijo;

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3 pb-3">
        <div>
          <CardTitle className="text-base">
            {t("cierre.af.title", "Activo fijo")}
          </CardTitle>
          <CardDescription>
            {t(
              "cierre.af.description",
              "Bienes de uso, amortización y revalúo fiscal (coef. {{coef}}).",
              { coef: coefRevaluacion },
            )}
          </CardDescription>
        </div>
        <Button size="sm" onClick={openAdd}>
          <Plus className="h-4 w-4" />
          <span className="ml-1.5">{t("cierre.af.add", "Agregar bien")}</span>
        </Button>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {t("cierre.af.empty", "Sin bienes registrados.")}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table className="min-w-[720px] text-sm">
              <TableHeader>
                <TableRow>
                  <TableHead>{t("cierre.af.col.name", "Bien")}</TableHead>
                  <TableHead className="text-right">
                    {t("cierre.af.col.year", "Año")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("cierre.af.col.cost", "Costo")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("cierre.af.col.netContable", "V. contable")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("cierre.af.col.revalued", "Revaluado")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("cierre.af.col.netFiscal", "V. fiscal")}
                  </TableHead>
                  <TableHead className="w-16" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.asset.id}>
                    <TableCell>
                      <div className="font-medium">{r.asset.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {CATEGORY_LABELS[r.asset.category]} ·{" "}
                        {r.asset.depreciationRatePct}%
                        {r.asset.isLand
                          ? ` · ${t("cierre.af.land", "terreno")}`
                          : ""}
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {r.asset.acquisitionYear}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {money(r.asset.costContableUyu)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {money(r.netContableUyu)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {money(r.revaluedCostUyu)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {money(r.netFiscalUyu)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          onClick={() => openEdit(r.asset)}
                          aria-label={t("common.edit", "Editar")}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-destructive hover:text-destructive"
                          onClick={() => remove.mutate(r.asset.id)}
                          disabled={remove.isPending}
                          aria-label={t("common.delete", "Eliminar")}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="border-t-2 font-semibold">
                  <TableCell>{t("cierre.af.total", "TOTAL")}</TableCell>
                  <TableCell />
                  <TableCell className="text-right tabular-nums">
                    {money(totals.costContableUyu)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {money(totals.netContableUyu)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {money(totals.revaluedCostUyu)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {money(totals.netFiscalUyu)}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editing
                ? t("cierre.af.editTitle", "Editar bien")
                : t("cierre.af.addTitle", "Agregar bien")}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="grid gap-1.5">
              <Label htmlFor="af-name">{t("cierre.af.f.name", "Nombre")}</Label>
              <Input
                id="af-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label>{t("cierre.af.f.category", "Categoría")}</Label>
                <Select
                  value={form.category}
                  onValueChange={(v) => setCategory(v as FixedAssetCategory)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c}>
                        {CATEGORY_LABELS[c]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="af-year">{t("cierre.af.f.year", "Año de alta")}</Label>
                <Input
                  id="af-year"
                  inputMode="numeric"
                  value={form.acquisitionYear}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, acquisitionYear: e.target.value }))
                  }
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label htmlFor="af-cost">{t("cierre.af.f.cost", "Costo (UYU)")}</Label>
                <Input
                  id="af-cost"
                  inputMode="decimal"
                  value={form.costContableUyu}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, costContableUyu: e.target.value }))
                  }
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="af-rate">{t("cierre.af.f.rate", "Tasa amort. %")}</Label>
                <Input
                  id="af-rate"
                  inputMode="decimal"
                  value={form.depreciationRatePct}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, depreciationRatePct: e.target.value }))
                  }
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label htmlFor="af-acc">
                  {t("cierre.af.f.openingContable", "Amort. acum. apertura")}
                </Label>
                <Input
                  id="af-acc"
                  inputMode="decimal"
                  value={form.openingAccumDepContableUyu}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      openingAccumDepContableUyu: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="af-revbase">
                  {t("cierre.af.f.revBase", "Base revaluada (opc.)")}
                </Label>
                <Input
                  id="af-revbase"
                  inputMode="decimal"
                  value={form.revaluationBaseUyu}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, revaluationBaseUyu: e.target.value }))
                  }
                />
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="af-accfiscal">
                {t("cierre.af.f.openingFiscal", "Amort. acum. fiscal apertura (opc.)")}
              </Label>
              <Input
                id="af-accfiscal"
                inputMode="decimal"
                value={form.openingAccumDepFiscalUyu}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    openingAccumDepFiscalUyu: e.target.value,
                  }))
                }
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={form.isLand}
                onCheckedChange={(v) =>
                  setForm((f) => ({ ...f, isLand: Boolean(v) }))
                }
              />
              {t("cierre.af.f.isLand", "Es terreno (no amortiza)")}
            </label>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setOpen(false)}
              disabled={save.isPending}
            >
              {t("common.cancel", "Cancelar")}
            </Button>
            <Button
              type="button"
              onClick={() => save.mutate()}
              disabled={save.isPending}
            >
              {save.isPending ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : null}
              {t("common.save", "Guardar")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
